import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import {
  fetchArticle,
  createArticle,
  updateArticle,
  publishArticle,
  unpublishArticle,
  sendForReview,
  scheduleArticle,
  unscheduleArticle,
  deleteArticle,
  uploadArticleImage,
} from './api';
import type {
  ArticleDetail,
  ArticleCategory,
  ArticleCluster,
  CreateArticleInput,
} from './types';

interface Props {
  articleId: string | null;
  onClose: () => void;
}

const CLUSTER_OPTIONS: { value: ArticleCluster; label: string }[] = [
  { value: 'WISSEN', label: 'Wissen' },
  { value: 'PRUEFUNG', label: 'Prüfung' },
  { value: 'KARRIERE', label: 'Karriere' },
  { value: 'BLOG', label: 'Blog' },
];

const CATEGORY_OPTIONS: { value: ArticleCategory; label: string }[] = [
  { value: 'GLOSSAR', label: 'Glossar' },
  { value: 'PRUEFUNG', label: 'Prüfung' },
  { value: 'KARRIERE', label: 'Karriere' },
  { value: 'RECHT', label: 'Recht' },
  { value: 'STUDIE', label: 'Studie' },
  { value: 'NEWS', label: 'News' },
  { value: 'METHODIK', label: 'Methodik' },
  { value: 'PRODUKT', label: 'Produkt' },
];

interface FormState {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  bodyMd: string;
  ogImageUrl: string;
  coverImageUrl: string;
  coverImageAlt: string;
  coverImageCredit: string;
  category: ArticleCategory;
  cluster: ArticleCluster;
  tagsInput: string;
  topicSlugsInput: string;
  changeNote: string;
}

const emptyForm: FormState = {
  slug: '',
  title: '',
  metaTitle: '',
  metaDescription: '',
  excerpt: '',
  bodyMd: '',
  ogImageUrl: '',
  coverImageUrl: '',
  coverImageAlt: '',
  coverImageCredit: '',
  category: 'STUDIE',
  cluster: 'WISSEN',
  tagsInput: '',
  topicSlugsInput: '',
  changeNote: '',
};

function articleToForm(a: ArticleDetail): FormState {
  return {
    slug: a.slug,
    title: a.title,
    metaTitle: a.metaTitle ?? '',
    metaDescription: a.metaDescription,
    excerpt: a.excerpt,
    bodyMd: a.bodyMd,
    ogImageUrl: a.ogImageUrl ?? '',
    coverImageUrl: a.coverImageUrl ?? '',
    coverImageAlt: a.coverImageAlt ?? '',
    coverImageCredit: a.coverImageCredit ?? '',
    category: a.category,
    cluster: a.cluster,
    tagsInput: a.tags.join(', '),
    topicSlugsInput: a.topicSlugs.join(', '),
    changeNote: '',
  };
}

function formToCreatePayload(form: FormState): CreateArticleInput {
  return {
    slug: form.slug.trim() || undefined,
    title: form.title.trim(),
    metaTitle: form.metaTitle.trim() || undefined,
    metaDescription: form.metaDescription.trim() || undefined,
    excerpt: form.excerpt.trim() || undefined,
    bodyMd: form.bodyMd,
    ogImageUrl: form.ogImageUrl.trim() || undefined,
    coverImageUrl: form.coverImageUrl.trim() || undefined,
    coverImageAlt: form.coverImageAlt.trim() || undefined,
    coverImageCredit: form.coverImageCredit.trim() || undefined,
    category: form.category,
    cluster: form.cluster,
    tags: form.tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    topicSlugs: form.topicSlugsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
  };
}

/**
 * ISO-String → Wert für `<input type="datetime-local">` (lokale Zeit, ohne Zone).
 * `toISOString().slice(...)` waere falsch: das liefert UTC und verschiebt den
 * angezeigten Termin im Sommer um zwei Stunden.
 */
function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type UploadTarget = 'body' | 'cover' | 'hero';

interface PendingUpload {
  file: File;
  previewUrl: string;
  alt: string;
  target: UploadTarget;
}

export function ArticleEditor({ articleId, onClose }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const isNew = !articleId;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [message, setMessage] = useState<{ text: string; variant: 'success' | 'error' } | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [scheduledInput, setScheduledInput] = useState('');
  const bodyFileInputRef = useRef<HTMLInputElement | null>(null);
  const coverFileInputRef = useRef<HTMLInputElement | null>(null);
  const heroFileInputRef = useRef<HTMLInputElement | null>(null);

  const query = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => fetchArticle(articleId!),
    enabled: !!articleId,
  });

  useEffect(() => {
    if (query.data) {
      setForm(articleToForm(query.data));
      setScheduledInput(toDatetimeLocal(query.data.scheduledFor));
    } else if (isNew) {
      setForm(emptyForm);
      setScheduledInput('');
    }
  }, [query.data, isNew]);

  const article = query.data;

  // Publish-Guards des Backends (checkPublishable) im UI gespiegelt — gilt
  // gleichermaessen fuer Sofort-Publish und Termin-Freigabe, damit ein Artikel
  // ohne Hero-Bild gar nicht erst eingeplant werden kann.
  const publishBlockReason = !form.coverImageUrl?.trim()
    ? 'Hero-Bild erforderlich, bevor publiziert werden kann.'
    : !form.coverImageAlt?.trim()
      ? 'Hero-Bild-Alt-Text erforderlich, bevor publiziert werden kann.'
      : null;

  const createMutation = useMutation({
    mutationFn: () => createArticle(formToCreatePayload(form)),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      setMessage({ text: 'Artikel erstellt — DRAFT.', variant: 'success' });
      // Switch to edit-mode on created article
      window.history.replaceState(null, '', `?article=${created.id}`);
    },
    onError: (err: Error) => setMessage({ text: err.message, variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateArticle(articleId!, {
        ...formToCreatePayload(form),
        changeNote: form.changeNote || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setForm((f) => ({ ...f, changeNote: '' }));
      setMessage({ text: 'Gespeichert.', variant: 'success' });
    },
    onError: (err: Error) => setMessage({ text: err.message, variant: 'error' }),
  });

  const publishMutation = useMutation({
    mutationFn: () => publishArticle(articleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setMessage({ text: 'Publiziert — Build-Webhook getriggert.', variant: 'success' });
    },
    onError: (err: Error) => setMessage({ text: err.message, variant: 'error' }),
  });

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishArticle(articleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setMessage({ text: 'Archiviert.', variant: 'success' });
    },
  });

  const reviewMutation = useMutation({
    mutationFn: () => sendForReview(articleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setMessage({ text: 'Status auf Review gesetzt.', variant: 'success' });
    },
  });

  const scheduleMutation = useMutation({
    // datetime-local liefert lokale Zeit ohne Zone ("2026-08-17T08:00").
    // new Date(...) interpretiert das als Ortszeit des Browsers — genau das ist
    // gemeint, wenn die Redaktion "Montag 08:00" plant. toISOString() macht
    // daraus UTC fuer die API.
    mutationFn: () => scheduleArticle(articleId!, new Date(scheduledInput).toISOString()),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setMessage({
        text: `Geplant für ${formatDateTime(updated.scheduledFor)} — geht dann automatisch live.`,
        variant: 'success',
      });
    },
    onError: (err: Error) => setMessage({ text: err.message, variant: 'error' }),
  });

  const unscheduleMutation = useMutation({
    mutationFn: () => unscheduleArticle(articleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setMessage({ text: 'Termin zurückgenommen — zurück im Entwurf.', variant: 'success' });
    },
    onError: (err: Error) => setMessage({ text: err.message, variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteArticle(articleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      onClose();
    },
  });

  const handleSave = (): void => {
    if (!form.title.trim()) {
      setMessage({ text: 'Title ist Pflicht.', variant: 'error' });
      return;
    }
    if (isNew) {
      createMutation.mutate();
    } else {
      updateMutation.mutate();
    }
  };

  const handleFileSelected = (file: File | null, target: UploadTarget): void => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Nur Bild-Dateien sind erlaubt.', variant: 'error' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: 'Datei zu gross (max 5 MB).', variant: 'error' });
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingUpload({ file, previewUrl, alt: '', target });
  };

  const closeUploadModal = (): void => {
    if (pendingUpload) URL.revokeObjectURL(pendingUpload.previewUrl);
    setPendingUpload(null);
    if (bodyFileInputRef.current) bodyFileInputRef.current.value = '';
    if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    if (heroFileInputRef.current) heroFileInputRef.current.value = '';
  };

  const confirmUpload = async (): Promise<void> => {
    if (!pendingUpload) return;
    const altTrimmed = pendingUpload.alt.trim();
    if (!altTrimmed) {
      setMessage({ text: 'Alt-Text ist Pflicht (Barrierefreiheit + SEO).', variant: 'error' });
      return;
    }
    setUploading(true);
    try {
      const targetId = articleId ?? 'new';
      const result = await uploadArticleImage(targetId, pendingUpload.file, altTrimmed);
      if (pendingUpload.target === 'hero') {
        setForm((f) => ({ ...f, coverImageUrl: result.url, coverImageAlt: altTrimmed }));
        setMessage({ text: 'Hero-Bild hochgeladen. Bitte speichern, um es zu uebernehmen.', variant: 'success' });
      } else if (pendingUpload.target === 'cover') {
        setForm((f) => ({ ...f, ogImageUrl: result.url }));
        setMessage({ text: 'OG-Image hochgeladen. Bitte speichern, um es zu uebernehmen.', variant: 'success' });
      } else {
        setForm((f) => ({
          ...f,
          bodyMd: f.bodyMd.endsWith('\n') || f.bodyMd === ''
            ? `${f.bodyMd}\n${result.markdown}\n`
            : `${f.bodyMd}\n\n${result.markdown}\n`,
        }));
        setMessage({ text: 'Bild hochgeladen + ins Markdown eingefuegt.', variant: 'success' });
      }
      closeUploadModal();
    } catch (err) {
      setMessage({ text: (err as Error).message, variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-center overflow-y-auto">
      <div className="flex w-full max-w-[1400px] flex-col rounded-none border-0 bg-bg-base shadow-2xl sm:mx-4 sm:my-4 sm:rounded-2xl sm:border sm:border-border-default">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div>
            <h2 className="text-lg font-semibold">
              {isNew ? 'Neuer Artikel' : article?.title ?? 'Artikel'}
            </h2>
            {article && (
              <p className="text-xs text-text-tertiary mt-0.5">
                Status: <strong>{article.status}</strong> · Updated{' '}
                {new Date(article.updatedAt).toLocaleString('de-DE')}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-2.5 text-sm text-text-secondary hover:bg-bg-elevated sm:py-1.5"
          >
            Schließen
          </button>
        </div>

        {/* Body */}
        <div className="grid lg:grid-cols-[1fr_320px] flex-1 overflow-hidden">
          {/* Editor */}
          <div className="overflow-y-auto p-6 space-y-4 border-r border-border-subtle">
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-1.5">
                Titel*
              </label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="z.B. Differentialdiagnose Dyspnoe"
                className="w-full px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-base"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-1.5">
                Slug
              </label>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="auto aus Titel — überschreibbar"
                className="w-full px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-sm font-mono"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-1.5">
                Excerpt
              </label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="1-2 Sätze für Hub-Pages und Snippets"
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-sm"
              />
            </div>

            <div data-color-mode="dark">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                  Body (Markdown)*
                </label>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFileSelected(file, 'body');
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition ${
                    dragActive
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border-default bg-bg-surface text-text-secondary hover:border-accent/50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => bodyFileInputRef.current?.click()}
                    className="font-semibold hover:text-accent"
                  >
                    + Bild hochladen
                  </button>
                  <span className="text-text-tertiary">oder Datei hier ablegen</span>
                  <input
                    ref={bodyFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    className="hidden"
                    onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null, 'body')}
                  />
                </div>
              </div>
              <div className="rounded-lg overflow-hidden border border-border-default">
                <MDEditor
                  value={form.bodyMd}
                  onChange={(val) => setForm({ ...form, bodyMd: val ?? '' })}
                  preview="live"
                  height={500}
                  visibleDragbar={false}
                  textareaProps={{
                    placeholder:
                      '# Überschrift\n\nFließtext, **fett**, *kursiv*, [Link](https://example.com)\n\n## Abschnitt\n\n- Liste\n- Punkt',
                  }}
                />
              </div>
              <p className="text-xs text-text-tertiary mt-1.5">
                GFM-Markdown (Tabellen, Strikethrough, Autolinks). Live-Preview rechts. HTML wird beim Speichern sanitized. Bilder via Upload-Button → R2-CDN. Alt-Text ist Pflicht (Barrierefreiheit + SEO).
              </p>
            </div>

            {!isNew && (
              <div>
                <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-1.5">
                  Change-Note (für Revision)
                </label>
                <input
                  value={form.changeNote}
                  onChange={(e) => setForm({ ...form, changeNote: e.target.value })}
                  placeholder="z.B. Faktencheck Cochrane-Review ergänzt"
                  className="w-full px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-sm"
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="overflow-y-auto p-6 bg-bg-surface/40 space-y-5">
            {/* SEO */}
            <details open className="rounded-lg bg-bg-elevated border border-border-subtle">
              <summary className="px-4 py-3 cursor-pointer text-sm font-semibold">SEO</summary>
              <div className="px-4 pb-4 space-y-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                    Meta-Title ({form.metaTitle.length}/60)
                  </label>
                  <input
                    value={form.metaTitle}
                    onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
                    placeholder="leer = Titel verwenden"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                    Meta-Description ({form.metaDescription.length}/155)
                  </label>
                  <textarea
                    value={form.metaDescription}
                    onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                    placeholder="Snippet-Text für Google"
                    rows={3}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm"
                  />
                </div>
              </div>
            </details>

            {/* Hero-/Cover-Bild (im Artikel sichtbar) */}
            <details open className="rounded-lg bg-bg-elevated border border-border-subtle">
              <summary className="px-4 py-3 cursor-pointer text-sm font-semibold">Hero-Bild (im Artikel)</summary>
              <div className="px-4 pb-4 space-y-3">
                {form.coverImageUrl ? (
                  <div className="space-y-2">
                    <div className="relative rounded-lg overflow-hidden border border-border-subtle bg-bg-surface">
                      <img
                        src={form.coverImageUrl}
                        alt="Hero-Preview"
                        className="w-full aspect-video object-cover"
                      />
                    </div>
                    <input
                      value={form.coverImageUrl}
                      onChange={(e) => setForm({ ...form, coverImageUrl: e.target.value })}
                      placeholder="URL (16:9 WebP)"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-[11px] font-mono"
                    />
                    <input
                      value={form.coverImageAlt}
                      onChange={(e) => setForm({ ...form, coverImageAlt: e.target.value })}
                      placeholder="Alt-Text (Pflicht für a11y + SEO)"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-xs"
                    />
                    <input
                      value={form.coverImageCredit}
                      onChange={(e) => setForm({ ...form, coverImageCredit: e.target.value })}
                      placeholder="Credit / Lizenzhinweis (optional)"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-xs"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => heroFileInputRef.current?.click()}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-bg-surface border border-border-default hover:bg-bg-elevated"
                      >
                        Ersetzen
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, coverImageUrl: '', coverImageAlt: '', coverImageCredit: '' })}
                        className="px-3 py-1.5 rounded-lg text-xs text-danger hover:bg-danger/10"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => heroFileInputRef.current?.click()}
                    className="w-full px-4 py-6 rounded-lg border border-dashed border-border-default text-sm text-text-secondary hover:border-accent/50 hover:text-accent transition"
                  >
                    + Hero-Bild hochladen
                    <p className="text-[11px] text-text-tertiary mt-1 font-normal">
                      16:9 (1600×900px) · wird oben im Artikel angezeigt
                    </p>
                  </button>
                )}
                <input
                  ref={heroFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null, 'hero')}
                />
              </div>
            </details>

            {/* OG-Image (Social-Vorschau) */}
            <details className="rounded-lg bg-bg-elevated border border-border-subtle">
              <summary className="px-4 py-3 cursor-pointer text-sm font-semibold">OG-Image (Social-Vorschau)</summary>
              <div className="px-4 pb-4 space-y-3">
                {form.ogImageUrl ? (
                  <div className="space-y-2">
                    <div className="relative rounded-lg overflow-hidden border border-border-subtle bg-bg-surface">
                      <img
                        src={form.ogImageUrl}
                        alt="Cover-Preview"
                        className="w-full aspect-[1200/630] object-cover"
                      />
                    </div>
                    <input
                      value={form.ogImageUrl}
                      onChange={(e) => setForm({ ...form, ogImageUrl: e.target.value })}
                      placeholder="URL"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-[11px] font-mono"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => coverFileInputRef.current?.click()}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-bg-surface border border-border-default hover:bg-bg-elevated"
                      >
                        Ersetzen
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, ogImageUrl: '' })}
                        className="px-3 py-1.5 rounded-lg text-xs text-danger hover:bg-danger/10"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => coverFileInputRef.current?.click()}
                    className="w-full px-4 py-6 rounded-lg border border-dashed border-border-default text-sm text-text-secondary hover:border-accent/50 hover:text-accent transition"
                  >
                    + Cover-Bild hochladen
                    <p className="text-[11px] text-text-tertiary mt-1 font-normal">
                      1200×630px empfohlen · wird als OG-Image fuer Social-Shares verwendet
                    </p>
                  </button>
                )}
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null, 'cover')}
                />
              </div>
            </details>

            {/* Klassifikation */}
            <details open className="rounded-lg bg-bg-elevated border border-border-subtle">
              <summary className="px-4 py-3 cursor-pointer text-sm font-semibold">Klassifikation</summary>
              <div className="px-4 pb-4 space-y-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                    Cluster (URL-Prefix)
                  </label>
                  <select
                    value={form.cluster}
                    onChange={(e) => setForm({ ...form, cluster: e.target.value as ArticleCluster })}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm"
                  >
                    {CLUSTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                    Kategorie
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value as ArticleCategory })
                    }
                    className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm"
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                    Topic-Slugs (App-Topics, kommagetrennt)
                  </label>
                  <input
                    value={form.topicSlugsInput}
                    onChange={(e) => setForm({ ...form, topicSlugsInput: e.target.value })}
                    placeholder="z.B. herz-kreislauf, anatomie"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                    Tags (kommagetrennt)
                  </label>
                  <input
                    value={form.tagsInput}
                    onChange={(e) => setForm({ ...form, tagsInput: e.target.value })}
                    placeholder="z.B. Differentialdiagnose, NYHA"
                    className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm"
                  />
                </div>
              </div>
            </details>

            {/* Aktionen */}
            <div className="rounded-lg bg-bg-elevated border border-border-subtle p-4 space-y-2">
              <button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="w-full px-4 py-2.5 rounded-lg font-semibold text-white bg-accent hover:bg-accent-hover disabled:opacity-50"
              >
                {isNew ? 'Erstellen (DRAFT)' : 'Speichern'}
              </button>

              {!isNew && article && (
                <>
                  {article.status === 'DRAFT' && (
                    <button
                      onClick={() => reviewMutation.mutate()}
                      className="w-full px-4 py-2 rounded-lg text-sm font-semibold bg-bg-surface border border-border-default hover:bg-bg-elevated"
                    >
                      → Zur Review
                    </button>
                  )}
                  {article.status === 'PUBLISHED' ? (
                    <button
                      onClick={() => unpublishMutation.mutate()}
                      className="w-full px-4 py-2 rounded-lg text-sm font-semibold bg-bg-surface border border-border-default hover:bg-bg-elevated"
                    >
                      Archivieren
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => publishMutation.mutate()}
                        disabled={!!publishBlockReason}
                        title={publishBlockReason ?? undefined}
                        className="w-full px-4 py-2 rounded-lg text-sm font-semibold text-white bg-success hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {article.status === 'SCHEDULED' ? 'Jetzt sofort publizieren' : 'Publizieren'}
                      </button>
                      {publishBlockReason && (
                        <p className="text-xs text-text-tertiary">{publishBlockReason}</p>
                      )}

                      {/* Termin-Freigabe */}
                      {article.status === 'SCHEDULED' ? (
                        <div className="rounded-lg bg-info-bg border border-border-subtle p-3 space-y-2">
                          <p className="text-xs text-text-secondary">
                            Geplant für{' '}
                            <strong className="text-text-primary">
                              {formatDateTime(article.scheduledFor)}
                            </strong>
                            . Geht dann automatisch live — die Website wird dabei neu gebaut und
                            zeigt den Artikel rund 15 Minuten später.
                          </p>
                          <button
                            onClick={() => unscheduleMutation.mutate()}
                            disabled={unscheduleMutation.isPending}
                            className="w-full px-4 py-2 rounded-lg text-sm font-semibold bg-bg-surface border border-border-default hover:bg-bg-elevated disabled:opacity-50"
                          >
                            Termin zurücknehmen
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2 pt-1">
                          <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold">
                            Oder: freigeben ab
                          </label>
                          <input
                            type="datetime-local"
                            value={scheduledInput}
                            onChange={(e) => setScheduledInput(e.target.value)}
                            className="w-full px-2.5 py-1.5 rounded-lg bg-bg-surface border border-border-default text-sm"
                          />
                          <button
                            onClick={() => scheduleMutation.mutate()}
                            disabled={
                              !scheduledInput || !!publishBlockReason || scheduleMutation.isPending
                            }
                            title={publishBlockReason ?? undefined}
                            className="w-full px-4 py-2 rounded-lg text-sm font-semibold bg-bg-surface border border-border-default hover:bg-bg-elevated disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Termin setzen
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => {
                      if (confirm('Wirklich löschen? Nicht reversibel.')) {
                        deleteMutation.mutate();
                      }
                    }}
                    className="w-full px-4 py-2 rounded-lg text-sm font-semibold text-danger hover:bg-danger/10"
                  >
                    Löschen
                  </button>
                </>
              )}
            </div>

            {message && (
              <div
                className={`rounded-lg px-4 py-3 text-sm ${
                  message.variant === 'success' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                }`}
              >
                {message.text}
              </div>
            )}

            {article?.revisions && article.revisions.length > 0 && (
              <details className="rounded-lg bg-bg-elevated border border-border-subtle">
                <summary className="px-4 py-3 cursor-pointer text-sm font-semibold">
                  Verlauf ({article.revisions.length})
                </summary>
                <div className="px-4 pb-4 space-y-2 text-xs">
                  {article.revisions.slice(0, 5).map((rev) => (
                    <div key={rev.id} className="text-text-tertiary">
                      <p>{new Date(rev.createdAt).toLocaleString('de-DE')}</p>
                      {rev.changeNote && <p className="italic">„{rev.changeNote}"</p>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>

        {pendingUpload && (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
            <div className="w-full max-w-lg rounded-2xl bg-bg-base border border-border-default shadow-2xl p-6">
              <h3 className="text-lg font-semibold mb-1">Bild einfuegen</h3>
              <p className="text-xs text-text-tertiary mb-4">
                Alt-Text ist Pflicht — kurze Beschreibung des Bildinhalts fuer Screenreader und Suchmaschinen.
              </p>

              <div className="rounded-lg overflow-hidden border border-border-subtle bg-bg-elevated mb-4 flex items-center justify-center" style={{ maxHeight: 280 }}>
                <img
                  src={pendingUpload.previewUrl}
                  alt="Preview"
                  className="max-w-full max-h-[280px] object-contain"
                />
              </div>

              <div className="text-xs text-text-tertiary mb-4 flex justify-between">
                <span>{pendingUpload.file.name}</span>
                <span>{(pendingUpload.file.size / 1024).toFixed(0)} KB · {pendingUpload.file.type}</span>
              </div>

              <label className="block text-[11px] uppercase tracking-wider text-text-tertiary font-semibold mb-1">
                Alt-Text*
              </label>
              <input
                value={pendingUpload.alt}
                onChange={(e) =>
                  setPendingUpload((p) => (p ? { ...p, alt: e.target.value } : p))
                }
                placeholder="z.B. EKG mit ST-Hebung in V2-V4 bei Vorderwandinfarkt"
                autoFocus
                className="w-full px-3 py-2 rounded-lg bg-bg-surface border border-border-default text-sm mb-4"
              />

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="px-4 py-2 rounded-lg text-sm text-text-secondary hover:bg-bg-elevated disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => void confirmUpload()}
                  disabled={uploading || !pendingUpload.alt.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-accent hover:bg-accent-hover disabled:opacity-50"
                >
                  {uploading ? 'Lade hoch…' : 'Hochladen + einfuegen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
